import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import TextField from '@mui/material/TextField';
import FormLabel from '@mui/material/FormLabel';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([ PropTypes.string, PropTypes.number, ]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,

  labelClassName: PropTypes.string,
  attachedText: PropTypes.string,
  error: PropTypes.array,
}

const defaultProps = {
  labelClassName: null,
  attachedText: null,
  type: "text",
}

// MUI replacement for the old react-bootstrap AppInput. Keeps the same public
// contract: a static label rendered above the field (styled via labelClassName,
// e.g. `.form-label` used by the UnimartBusiness dark form), an optional prefix
// (`attachedText`), an array `error` prop, and the password visibility toggle.
const AppInput = ({ label, error, attachedText, labelClassName, name, value, onChange, placeholder, type, ...rest }) => {
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const getInputType = (visible) => {
    if (type === "password") {
      return visible ? "text" : "password";
    }
    return type;
  };

  const inputType = useMemo( () => getInputType(showPassword), [showPassword, type] );

  const errorMessage = error ? error.join(", ") : null;

  return (
    <React.Fragment>
      { label &&
        <FormLabel component="label" className={ labelClassName } sx={{ display: "block", mb: 0.5 }}>
          { label }
        </FormLabel>
      }
      <TextField
        name={ name }
        value={ value }
        onChange={ onChange }
        placeholder={ placeholder }
        type={ inputType }
        fullWidth
        size="small"
        error={ !!error }
        helperText={ errorMessage || undefined }
        inputProps={{ ...rest }}
        InputProps={{
          startAdornment: attachedText
            ? <InputAdornment position="start">{ attachedText }</InputAdornment>
            : undefined,
          endAdornment: type === "password"
            ? (
              <InputAdornment position="end">
                <IconButton onClick={ togglePasswordVisibility } edge="end" size="small">
                  { showPassword ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" /> }
                </IconButton>
              </InputAdornment>
            )
            : undefined,
        }}
      />
    </React.Fragment>
  )
};

AppInput.propTypes = propTypes;
AppInput.defaultProps = defaultProps;

export default AppInput;
